import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardBody, Spinner, Button } from "reactstrap";
import { getCkycStatus } from "./OrderService";
import { showToast } from "../../../common/Toaster/Toaster";
import BreadCrumb from "../../../Components/Common/BreadCrumb";

const KycReturn = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderId = searchParams.get("order_id");
  const memberId = searchParams.get("member_id");

  const [loading, setLoading] = useState(false);
  const [kycStatus, setKycStatus] = useState("");
  const [ckycNumber, setCkycNumber] = useState("");
  const [ckycRemarks, setCkycRemarks] = useState("");
  const [canRetry, setCanRetry] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const checkStatus = async () => {
    if (!orderId || !memberId) {
      setErrorMsg("Missing order_id or member_id in the URL.");
      return;
    }
    setLoading(true);
    setErrorMsg("");
    try {
      const response = await getCkycStatus(memberId, orderId);
      const data = response.data;
      setKycStatus(data.kyc_status);
      setCkycRemarks(data.ckyc_remarks || "");
      setCanRetry(!!data.can_retry);
      if (data.kyc_status === "Completed") {
        setCkycNumber(data.ckyc_number);
        showToast("KYC completed successfully!", "success");
      } else if (data.kyc_status === "Failed") {
        showToast("KYC verification failed.", "error");
      } else {
        showToast("KYC status is pending.", "info");
      }
    } catch (error) {
      console.error("CKYC status check failed:", error?.message);
      const msg = error.response?.data?.error_message || "Failed to fetch KYC status.";
      setErrorMsg(msg);
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkStatus();
  }, [orderId, memberId]);

  return (
    <div className="page-content">
      <BreadCrumb items={[
        { title: "Orders", link: "/order/orders" },
        { title: `Order ${orderId || ""}`, link: `/order/orders/update/${orderId}` },
        { title: "KYC Return", link: "" }
      ]} />
      
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <Card className="shadow-lg border-0 rounded-4 overflow-hidden" style={{ maxWidth: "500px", width: "100%" }}>
          <div className="p-4 text-center text-white position-relative" style={{ background: "linear-gradient(135deg, #026AFA 0%, #014FB8 100%)" }}>
            <h4 className="text-white mb-1">CKYC Verification Status</h4>
            <p className="text-white-50 mb-0">Order ID: {orderId}</p>
          </div>
          <CardBody className="p-5 text-center">
            {loading ? (
              <div className="py-4">
                <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                <p className="mt-3 text-muted">Retrieving your CKYC status. Please wait...</p>
              </div>
            ) : (
              <div>
                {kycStatus === "Completed" && (
                  <div className="animate__animated animate__fadeIn">
                    <div className="mb-4">
                      <span className="badge bg-success-subtle text-success p-3 rounded-circle" style={{ fontSize: "2rem" }}>
                        ✓
                      </span>
                    </div>
                    <h3 className="text-success mb-2">KYC Completed!</h3>
                    <p className="text-muted">Your Central KYC has been successfully verified.</p>
                    {ckycNumber && (
                      <div className="bg-light p-3 rounded-3 mb-4">
                        <small className="text-muted d-block">CKYC Number</small>
                        <strong className="fs-5">{ckycNumber}</strong>
                      </div>
                    )}
                    <Button color="primary" className="w-100 rounded-3" onClick={() => navigate(`/order/orders/update/${orderId}`)}>
                      Go to Order
                    </Button>
                  </div>
                )}

                {kycStatus === "Failed" && (
                  <div className="animate__animated animate__fadeIn">
                    <div className="mb-4">
                      <span className="badge bg-danger-subtle text-danger p-3 rounded-circle" style={{ fontSize: "2rem" }}>
                        ✗
                      </span>
                    </div>
                    <h3 className="text-danger mb-2">KYC Verification Failed</h3>
                    {ckycRemarks && <p className="text-dark fw-medium mb-3">Remarks: {ckycRemarks}</p>}
                    <p className="text-muted">The registry could not verify your details. Please try again.</p>
                    <div className="d-flex gap-2">
                      <Button color="outline-primary" className="flex-fill rounded-3" onClick={() => navigate(`/order/orders/update/${orderId}`)}>
                        Back to Order
                      </Button>
                      <Button color="primary" className="flex-fill rounded-3" onClick={checkStatus}>
                        Retry Status Check
                      </Button>
                    </div>
                  </div>
                )}

                {kycStatus === "Pending" && (
                  <div className="animate__animated animate__fadeIn">
                    <div className="mb-4">
                      <span className="badge bg-warning-subtle text-warning p-3 rounded-circle" style={{ fontSize: "2rem" }}>
                        ⏳
                      </span>
                    </div>
                    <h3 className="text-warning mb-2">KYC Still Pending</h3>
                    {ckycRemarks && <p className="text-dark fw-medium mb-3">Remarks: {ckycRemarks}</p>}
                    <p className="text-muted">The verification is currently being processed by the insurer registry.</p>
                    <div className="d-flex gap-2">
                      <Button color="outline-primary" className="flex-fill rounded-3" onClick={() => navigate(`/order/orders/update/${orderId}`)}>
                        Back to Order
                      </Button>
                      <Button color="primary" className="flex-fill rounded-3" onClick={checkStatus}>
                        Check Status Again
                      </Button>
                    </div>
                  </div>
                )}

                {!kycStatus && errorMsg && (
                  <div className="animate__animated animate__fadeIn">
                    <div className="mb-4">
                      <span className="badge bg-danger-subtle text-danger p-3 rounded-circle" style={{ fontSize: "2rem" }}>
                        !
                      </span>
                    </div>
                    <h3 className="text-danger mb-2">Error Occurred</h3>
                    <p className="text-danger">{errorMsg}</p>
                    <Button color="primary" className="w-100 rounded-3" onClick={checkStatus}>
                      Retry
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default KycReturn;
